import { rpcClient } from '@/api/rpc/client';
import type { MasterlistSummary } from '@erp/rpc';
import { stageUpload } from './stageUpload';
import { workbookContentType } from './workbookContentType';

/**
 * Upload the PCSC Masterlist and get back what is now loaded.
 *
 * The bytes go straight to blob storage: `getUploadUrl` reserves a presigned
 * staging URL, the workbook is PUT there raw (an XHR, so the upload has a
 * percentage), and `confirmUpload` claims the staged blob. Nothing binary
 * passes through the API server.
 *
 * Reading the workbook happens inside `confirmUpload`, which is why that call
 * takes a moment — it is the first request on the server that can see the file.
 */
export async function uploadPcscMasterlist(
  file: File,
  /**
   * Transfer progress, and then `null` once the bytes are sent.
   *
   * The null is what tells a caller the wait has changed character: the upload
   * is done and the server is now reading the workbook, which is the slower
   * half and has no percentage to report.
   */
  onUploadProgress?: (percent: number | null) => void,
): Promise<MasterlistSummary> {
  const uploadKey = await stageUpload({
    file,
    contentType: workbookContentType(file),
    getUploadUrl: rpcClient.pricing.masterlist.getUploadUrl,
    onUploadProgress,
  });

  onUploadProgress?.(null);

  return rpcClient.pricing.masterlist.confirmUpload({
    uploadKey,
    fileName: file.name,
  });
}
